import { readCookie } from "./functions";

type ClarityFn = (...args: unknown[]) => void;

/**
 * Clarity accumulates values for a custom key rather than replacing them, and
 * custom tags are quota-limited per session. Several call sites fire on the same
 * page load (app boot, the admin navbar's two client-detail effects, the
 * depot-connect route), so remember what was last sent and skip an identical
 * repeat. Re-tags normally when the client changes — e.g. a different login.
 */
let lastTaggedSignature: string | null = null;

/**
 * The KYC status space, keyed by string so a cookie carrying either the number
 * 1 or the string "1" maps the same way — PickupForm compares these as strings
 * (PickupForm.jsx:1355-1368), so both forms occur.
 */
const KYC_STATUS_LABELS: Record<string, string> = {
	"0": "pending",
	"1": "verified",
	"2": "rejected",
};

/**
 * Label the KYC status, or return undefined for anything unrecognised.
 *
 * Deliberately does not fall back to "pending": that would tag rejected sellers
 * and sellers whose cookie lacks the field as pending, so a Clarity segment on
 * pending would be a mix of three different populations. It would also disagree
 * with GA4 and the events API, where gaBackend.deriveKycStatus sends "" for the
 * same unknowns — the same session would then carry two different answers.
 *
 * customKeys skips undefined values, so an unknown status is simply not tagged.
 */
const toKycStatusLabel = (status: unknown): string | undefined => {
	if (status === null || status === undefined) {
		return undefined;
	}
	return KYC_STATUS_LABELS[String(status)];
};

/** Clarity rejects non-string custom key values, so everything is stringified. */
const toClarityValue = (value: unknown): string | undefined => {
	if (value === null || value === undefined) {
		return undefined;
	}
	return String(value);
};

/**
 * Records a Clarity custom event, used as a funnel step in the dashboard.
 *
 * Clarity's `event` command takes an event NAME and nothing else — a second
 * argument is accepted by the signature and then discarded, so per-event
 * properties cannot be attached. Dimensions for segmenting a funnel have to be
 * session-scoped custom tags instead (see setClarityCustomKeys), which is why
 * kyc_status is tagged there rather than passed here.
 *
 * No-ops when the snippet was never installed or was blocked.
 *
 * @param eventName - Must match the funnel step name configured in Clarity.
 */
export const trackClarityEvent = (eventName: string): void => {
	const clarity = (window as unknown as { clarity?: ClarityFn }).clarity;
	if (typeof clarity !== "function" || !eventName) {
		return;
	}

	try {
		clarity("event", eventName);
	} catch {
		// Analytics must never break a user flow.
	}
};

/**
 * Identifies the client to Microsoft Clarity and pushes the custom keys.
 *
 * Reads the `client` cookie directly, so it can run from any layout — including
 * routes that never mount the admin navbar (e.g. /depot-connect-v2). No-ops when
 * the Clarity snippet was not installed (non-production hosts, blocked script),
 * when the cookie is missing, or when the same values were already sent.
 */
export const setClarityCustomKeys = (): void => {
	const clarity = (window as unknown as { clarity?: ClarityFn }).clarity;
	if (typeof clarity !== "function") {
		return;
	}

	// readCookie JSON.parses the cookie; this runs at module scope in index.jsx,
	// so an unparseable cookie would otherwise blank the whole app on boot.
	let client;
	try {
		client = readCookie("client", true);
	} catch {
		return;
	}
	if (!client) {
		return;
	}

	const clientDetails = client?.client_poc?.client;
	const customKeys = [
		{ key: "client_email", value: toClarityValue(client?.email) },
		{
			key: "onlable",
			value: toClarityValue(clientDetails?.is_label_generation_enabled),
		},
		{ key: "wallet_mode", value: toClarityValue(clientDetails?.is_on_credit) },
		{ key: "client_source", value: clientDetails?.sr_id ? "SR" : "PR" },
		{ key: "client_sr_id", value: toClarityValue(clientDetails?.sr_id) },
		{ key: "client_rb_id", value: toClarityValue(clientDetails?.id) },
		// Funnel segmentation (RB-7549). Sent as a session tag rather than an
		// event property because clarity("event", …) takes a name only — see
		// trackClarityEvent above.
		{
			key: "kyc_status",
			value: toKycStatusLabel(clientDetails?.kyc_status),
		},
	];

	const customId = toClarityValue(clientDetails?.id);

	if (customId !== undefined) {
		// Clarity scopes `identify` to the page it is called on, and an SPA route
		// change counts as a new page — so this deliberately sits outside the
		// dedupe below and re-fires on every navigation (index.jsx listens on the
		// history). Re-sending the same id for a page is a no-op; skipping it
		// leaves every page after the landing one without a customised user id,
		// which is why only single-page sessions (/depot-connect-v2) showed it.
		//
		// Clarity masks the custom user id in the dashboard (shows `70****`). The
		// 5th `friendlyName` argument is rendered unmasked, so send the id there
		// too; args 2 and 3 (custom session/page id) are left to Clarity.
		clarity("identify", customId, undefined, undefined, customId);
	}

	// Custom tags accumulate per session and are quota-limited, so unlike
	// `identify` they are sent only when the values actually change.
	const signature = JSON.stringify([customId, customKeys]);
	if (signature === lastTaggedSignature) {
		return;
	}
	lastTaggedSignature = signature;

	customKeys.forEach(({ key, value }) => {
		if (value !== undefined) {
			clarity("set", key, value);
		}
	});
};
